import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { fetchUniqueDocs, saveUniqueDocs } from "../services/uniqueDocs.service"
import { useEffect, useState } from "react"
import { v4 as uuidv4 } from "uuid"
import Swal from "sweetalert2"

export const useUniqueDocs = () => {
    const queryKey = ["uniqueDocsByClient"]
    const queryClient = useQueryClient()
    
    const {
        data,
        isLoading,
        isError,
        refetch,
    } = useQuery({
        queryKey,
        queryFn: fetchUniqueDocs,
        staleTime: 1000 * 60 * 5,
        retry: 2,
        refetchOnMount: true,
        placeholderData: keepPreviousData,
    })

    useEffect(() => {
        if (isLoading) {
            Swal.fire({
                footer: 'Loading, please wait',
                showConfirmButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading()
                },
            })
        } else {
            Swal.close()
        }
    }, [isLoading])

    const saveDocumentsMutation = useMutation({
        mutationFn: saveUniqueDocs,
        onMutate: () => {
            Swal.fire({
                footer: 'Loading, please wait',
                showConfirmButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading()
                },
            })
        },
        onSuccess: () => {
            Swal.fire({
                icon: 'success',
                text: 'Information saved successfully',
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
            })
            
            setDirtyDocs({})
            setCreatedDocs([])
            queryClient.invalidateQueries({ queryKey: ["uniqueDocsByClient"] })
            // The backend creates DocumentCompliance records for all eligible suppliers
            // after saving unique docs, so invalidate all documentCompliance queries.
            queryClient.invalidateQueries({ queryKey: ["documentCompliance"] })
        },
        onError: (error) => {
            Swal.fire({
                icon: 'error',
                text: 'Failed to save information',
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
            })
            console.error("Error al guardar los documentos únicos por cliente:", error)
        }
    })

    // Map of _id -> partial changes for existing docs that were modified
    const [dirtyDocs, setDirtyDocs] = useState({})
    // New docs not yet persisted to the DB (identified by _tempId)
    const [createdDocs, setCreatedDocs] = useState([])

    const handleDocumentChange = (id, field, value) => {
        const isNewDoc = createdDocs.some(d => d._id === id)

        if (isNewDoc) {
            setCreatedDocs(prev =>
                prev.map(d => d._id === id ? { ...d, [field]: value } : d)
            )
        } else {
            setDirtyDocs(prev => ({
                ...prev,
                [id]: { ...(prev[id] ?? {}), [field]: value },
            }))
        }
    }

    const addNewDocument = () => {
        const newDoc = {
            _id: uuidv4(), // Temporary ID for React rendering, replace with real ID upon saving
            documentName: "",
            periodicity: 0,
            country: "MEX",
            typePerson: "",
            documentDisclaimer: "",
            notApplicable: false,
            supplierCompliancy: true,
        }
        setCreatedDocs(prev => [...prev, newDoc])
    }

    const discardChanges = () => {
        setDirtyDocs({})
        setCreatedDocs([])
    }

    // Merge dirty changes into the original docs for rendering
    const uniqueDocs = [
        ...(data ?? []).map(doc => dirtyDocs[doc._id] ? { ...doc, ...dirtyDocs[doc._id] } : doc),
        ...createdDocs,
    ]

    // Build the final updatedDocs list from dirty entries merged with original data
    const updatedDocs = (data ?? [])
        .filter(doc => dirtyDocs[doc._id])
        .map(doc => ({ ...doc, ...dirtyDocs[doc._id] }))

    const modifiedUniqueDocs = {
        updatedDocs,
        createdDocs,
    }

    const hasPendingChanges = Object.keys(dirtyDocs).length > 0 || createdDocs.length > 0

    const canSave = hasPendingChanges &&
        createdDocs.every(d => d.documentName.trim() !== "" && d.typePerson !== "")

    const handleSaveChanges = () => {
        const normalizePeriodicity = (doc) => ({
            ...doc,
            periodicity: doc.periodicity === "" ? 0 : doc.periodicity,
        })

        // Strip _id from new docs before sending to the server
        const docsToCreate = createdDocs.map(({ _id, ...rest }) => normalizePeriodicity(rest))
        const docsToUpdate = updatedDocs.map(normalizePeriodicity)

        const payload = {
            updatedDocs: docsToUpdate,
            createdDocs: docsToCreate,
        }

        saveDocumentsMutation.mutate(payload)
    }

    return {
        uniqueDocs,
        isLoading,
        isError,
        refetch,
        modifiedUniqueDocs,
        hasPendingChanges,
        canSave,
        handleDocumentChange,
        addNewDocument,
        discardChanges,
        handleSaveChanges,
    }
}